import { readonly, ref } from 'vue'
// @ts-expect-error importing untyped JS module into TypeScript
import loadjs from 'loadjs'
// @ts-expect-error importing untyped JS module into TypeScript
import loginClient from '@/services/clients/login'

type IntercomCommand =
  | 'boot'
  | 'update'
  | 'shutdown'
  | 'showSpace'
  | 'hide'
  | 'onShow'
  | 'onHide'
  | 'onUnreadCountChange'

/** Messenger spaces the console opens directly. */
export type IntercomSpace = 'home' | 'messages' | 'help'

declare global {
  interface Window {
    Intercom?: (_command: IntercomCommand, _arg?: unknown) => void
  }
}

interface IntercomBootSettings {
  app_id: string
  api_base: string
  hide_default_launcher: boolean
  intercom_user_jwt?: string
}

const APP_ID: string | undefined = import.meta.env.VITE_INTERCOM_APP_ID
const API_BASE = 'https://api-iam.intercom.io'

let isMessengerOpen = false
let showDefaultLauncher = false
let loadPromise: Promise<void> | null = null

const unreadCount = ref(0)

/** Conversations with replies the person has not read yet. Zero until Intercom has loaded. */
export const intercomUnreadCount = readonly(unreadCount)

/** Whether this build is configured with an Intercom workspace to open. */
export function isIntercomAvailable (): boolean {
  return Boolean(APP_ID)
}

/**
 * Loads and boots the Messenger without opening it, so unread replies and News badges can reach
 * the person before they think to look. Failures only warn: support chat is never worth an error.
 */
export function startIntercom (): void {
  loadIntercom().catch(e => console.warn('Failed to load Intercom', e))
}

/**
 * Opens the Messenger on the given space. Always name one: Intercom's plain `show` reopens
 * whichever space was last open, so chat could land on help articles.
 */
export function openIntercom (space: IntercomSpace): void {
  runIntercomCommand(['showSpace', space])
}

/** Opens the Messenger where someone wanting to talk to us should land. */
export function openSupportChat (): void {
  openIntercom(supportChatSpace())
}

export function toggleSupportChat (): void {
  if (isMessengerOpen) {
    runIntercomCommand(['hide'])
  } else {
    openSupportChat()
  }
}

/** Messages when a reply is waiting, so it is right there; otherwise Home, to start a new one. */
function supportChatSpace (): IntercomSpace {
  return unreadCount.value > 0 ? 'messages' : 'home'
}

/**
 * Shows or hides Intercom's own floating launcher. The header's chat button is always there; the
 * floating one is extra reach for organizations still finding their way around.
 */
export function setIntercomLauncherVisible (visible: boolean): void {
  showDefaultLauncher = visible
  // Before boot, the boot settings pick this up. After it, tell the running Messenger.
  loadPromise
    ?.then(() => intercom()('update', { hide_default_launcher: !visible }))
    .catch(() => {})
}

export function shutdownIntercom (): void {
  window.Intercom?.('shutdown')
  loadPromise = null
  isMessengerOpen = false
  unreadCount.value = 0
}

function runIntercomCommand ([command, arg]: [IntercomCommand, unknown?]): void {
  loadIntercom()
    .then(() => intercom()(command, arg))
    .catch(e => console.warn('Failed to load Intercom', e))
}

function intercom (): NonNullable<Window['Intercom']> {
  if (!window.Intercom) throw new Error('Intercom is not loaded')
  return window.Intercom
}

function loadIntercom (): Promise<void> {
  if (!APP_ID) return Promise.reject(new Error('VITE_INTERCOM_APP_ID is not set'))
  if (!loadPromise) {
    loadPromise = loadScript(APP_ID).then(() => bootIntercom(APP_ID)).catch(e => {
      loadPromise = null
      throw e
    })
  }
  return loadPromise
}

function loadScript (appId: string): Promise<void> {
  if (loadjs.isDefined('intercom')) return Promise.resolve()
  return loadjs([`https://widget.intercom.io/widget/${appId}`], 'intercom', {
    numRetries: 3,
    returnPromise: true
  })
}

async function fetchJwt (): Promise<string | undefined> {
  try {
    const response = await loginClient.post('intercom/messaging/jwt')
    return response.data.jwt
  } catch (error) {
    // If JWT retrieval fails, continue as anonymous visitor.
    console.warn('Failed to login Intercom messenger user', error)
    return undefined
  }
}

async function bootIntercom (appId: string): Promise<void> {
  const jwt = await fetchJwt()
  const settings: IntercomBootSettings = {
    app_id: appId,
    api_base: API_BASE,
    // The header has its own chat button, so the floating launcher is opt-in.
    hide_default_launcher: !showDefaultLauncher,
    ...(jwt ? { intercom_user_jwt: jwt } : {})
  }
  intercom()('boot', settings)
  intercom()('onShow', () => { isMessengerOpen = true })
  intercom()('onHide', () => { isMessengerOpen = false })
  intercom()('onUnreadCountChange', (count: number) => { unreadCount.value = count })
}
